import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const category = article ? getCategoryBySlug(article.category) : null;
  const relatedArticles = article ? getRelatedArticles(article.slug, article.category, 3) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center py-20">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5 rounded-full bg-background-100">
                <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
              <p className="text-foreground-500 mb-6">该文章可能已被移除或链接无效</p>
              <Link
                to="/"
                className="inline-flex px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '91免费',
    },
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb />

            <article>
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {article.isHot && (
                    <span className="text-xs px-2 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                      热门
                    </span>
                  )}
                  {article.isNew && (
                    <span className="text-xs px-2 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                      最新
                    </span>
                  )}
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-xs px-2 py-1 rounded-full bg-primary-50 text-primary-600 font-medium hover:bg-primary-100 transition-colors whitespace-nowrap"
                    >
                      {category.name}
                    </Link>
                  )}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span>{article.author}</span>
                  <span className="text-foreground-300">|</span>
                  <span>{article.date}</span>
                  <span className="text-foreground-300">|</span>
                  <span>{article.readTime} 分钟阅读</span>
                </div>
              </header>

              <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="mt-14 mb-14">
                <h2 className="text-lg font-bold text-foreground-950 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <Footer />
    </>
  );
}